import { useGetScenariosQuery } from "api/TenantSlug/ConnectionCreator/hooks";
import { AddFormStructureScenarioDrawer } from "components/Drawers/AddFormStructureScenarioDrawer/AddFormStructureScenarioDrawer";
import { Table } from "components/UI/Table";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateCell } from "components/UI/Table/UI/DateCell/DateCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useSmartRowHandlers } from "hooks/useTableRowHandlers.tsx";
import { useRecoilValue } from "recoil";
import { ConnectionCreatorScenarioCategory, PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models.ts";

import ScenarioData = App.Data.AppScenarioData;
import GetScenariosResponse = Paldock.LaravelConnectionCreator.Http.Responses.GetScenariosResponse;

export const ScenarioFormStructureTable = () => {
    const tableId = TableId.ScenarioFormStructureTable;
    const tableAtomInstance = tableAtom<ScenarioData>();
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGeneral } = usePaldockTranslation("general");
    const { t: tCC } = usePaldockTranslation("components.connection-creator");
    const permissionsUtils = usePermission();
    const tableState = useRecoilValue(tableAtomInstance(tableId));

    const request = {
        filter: {
            filter: {
                category: [
                    ConnectionCreatorScenarioCategory.StructureValidate,
                    ConnectionCreatorScenarioCategory.StructureModify,
                    ConnectionCreatorScenarioCategory.StructureAutocomplete,
                ].join(","),
                ...(tableState.filter?.filter || {}),
            },
            sort: tableState.filter?.sort,
        },
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
    };

    const getScenariosQuery = useGetScenariosQuery(request, { enabled: false });

    const tableFetchDataUtils = useTableDataApiOperations<GetScenariosResponse, void, Record<string, never>, never>(
        tableId,
        getScenariosQuery,
        undefined,
        undefined,
        undefined
    );

    const columnConfig: TableColumnType<ScenarioData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "title",
            key: "title",
            inputType: "text",
            sorter: true,
            defaultSorter: true,
            ellipsis: true,
            width: 200,
            filterable: true,
            filterType: FILTER_TYPES.string,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("status")} />,
            dataIndex: "is_enabled",
            key: "is_enabled",
            render: (isEnabled: boolean) => (isEnabled ? tGeneral("status.enabled") : tGeneral("status.disabled")),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterOptions: [
                { label: tGeneral("status.enabled"), value: "true" },
                { label: tGeneral("status.disabled"), value: "false" },
            ],
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tCC("enums.scenario_category.label")} />,
            dataIndex: "category",
            key: "category",
            render: (v: ConnectionCreatorScenarioCategory) => {
                const keys: Partial<Record<ConnectionCreatorScenarioCategory, string>> = {
                    [ConnectionCreatorScenarioCategory.StructureValidate]: "enums.scenario_category.structure_validate",
                    [ConnectionCreatorScenarioCategory.StructureModify]: "enums.scenario_category.structure_modify",
                    [ConnectionCreatorScenarioCategory.StructureAutocomplete]:
                        "enums.scenario_category.structure_autocomplete",
                };
                return keys[v] ? tCC(keys[v]!) : <EmptyCell />;
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterOptions: [
                {
                    label: tCC("enums.scenario_category.structure_validate"),
                    value: ConnectionCreatorScenarioCategory.StructureValidate,
                },
                {
                    label: tCC("enums.scenario_category.structure_modify"),
                    value: ConnectionCreatorScenarioCategory.StructureModify,
                },
                {
                    label: tCC("enums.scenario_category.structure_autocomplete"),
                    value: ConnectionCreatorScenarioCategory.StructureAutocomplete,
                },
            ],
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("created_at")} />,
            dataIndex: "created_at",
            key: "created_at",
            render: (date: string) => <DateCell value={date} />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.date,
            ellipsis: true,
            width: 120,
        },
    ];

    const { getRowHandlers, getRowClassName } = useSmartRowHandlers<ScenarioData>({
        entityName: "scenario",
        editPermission: PermissionEnum.ToolScenarioFieldGeneral,
        detailPermission: PermissionEnum.ToolScenarioField,
        editUrl: "/$tenantSlug/tool/validation/:id/general",
    });

    return (
        <>
            {permissionsUtils.hasPermission(PermissionEnum.ToolScenarioField) && (
                <AddFormStructureScenarioDrawer onSubmit={() => tableFetchDataUtils.refetchData()} />
            )}
            <Table<GetScenariosResponse, ScenarioData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetScenariosResponse}
                dataSource={tableFetchDataUtils.tableData?.scenarios ?? []}
                columnConfig={columnConfig}
                loading={getScenariosQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                addEntityData={{
                    drawerId: DrawerId.AddFormStructureScenario,
                    permission: PermissionEnum.ToolScenarioField,
                }}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                hideActionsColumn={true}
                autoScrollWidth={true}
                enableFilters={true}
            />
        </>
    );
};
